/**
 * Types globaux de l'application (navigation, notifications, préférences)
 */

import type { GameTable } from './game';

// ============================================
// Navigation par onglets
// ============================================

/** Identifiants des onglets principaux */
export type TabId = 'home' | 'chat' | 'tables' | 'game' | 'rankings' | 'competitions' | 'settings' | 'training' | 'library' | 'forums' | 'admin' | 'subscription' | 'mySubscription';

export type LibrarySection = 'own' | 'my_shares' | 'shared_public' | 'shared_private';

export type LibraryOpenMode = 'replay' | 'play' | 'poll';

export interface LibraryNavigationRequest {
  section?: LibrarySection;
  sharedGameId?: number;
  mode?: LibraryOpenMode;
  pendingPollsOnly?: boolean;
  /** Naviguer sans ouvrir la partie (surbrillance dans la liste) */
  highlightOnly?: boolean;
  /** Partie personnelle à surligner dans « Mes parties » */
  ownGameId?: number;
}

export type DiffereCompetitionType = 'indiv' | 'tripl' | 'quad';

/** Navigation profonde vers les résultats différé (depuis l'historique entraînement) */
export interface DiffereNavigationRequest {
  diffType: DiffereCompetitionType;
  differeId: number;
  playerPseudo?: string;
  playerDisplay?: string;
  dealNum?: number;
  partieId?: number;
  view?: 'replay' | 'donnes';
}

// ============================================
// Mode entraînement
// ============================================

/** Qualité de main souhaitée (barème Chavey) */
export type HandQuality = 'random' | 'passe' | 'petite' | 'garde' | 'garde_sans' | 'garde_contre';

/** Contraintes de distribution pour le mode entraînement */
export interface TrainingDealConstraints {
  /** Qualité de main souhaitée par siège (0=Sud, 1=Est, 2=Nord, 3=Ouest) */
  handQualities?: Record<number, HandQuality>;
  /** Forcer un bot à enchérir un contrat spécifique */
  forcedBids?: Record<number, number>;
  /** Siège auquel la distribution doit donner une main de preneur (0=Sud) */
  taker?: number;
  /** Contrat visé pour ce siège (seuil Chavey classique) */
  takerContract?: number;
  /** Score Chavey brut : prioritaire sur takerContract quand > 0 */
  takerMinBidScore?: number;
  /** Les autres sièges passent d'office, le preneur annonce takerContract */
  guaranteeTaker?: boolean;
}

/** Configuration des bots en mode entraînement */
export interface TrainingBotConfig {
  /** Sièges où les bots sont désactivés (l'utilisateur joue à leur place) */
  disabledBots: Set<number>;
}

export type TrainingAIModelSlot = 'production' | 'beta' | 'legacy-rv';

export interface TrainingAIModelSelection {
  attacker: TrainingAIModelSlot;
  defender: TrainingAIModelSlot;
  bidding: TrainingAIModelSlot;
  discard: TrainingAIModelSlot;
}

/** Mode d'affichage du tapis en entraînement */
export type TrainingDisplayMode = 'classic' | 'cards_on_table';

/** Informations de la session d'entraînement active */
export interface TrainingInfo {
  /** ID unique de la session */
  sessionId: string;
  /** Mode d'affichage actuel */
  displayMode: TrainingDisplayMode;
  /** Afficher le résumé des plis à droite */
  showTimeline: boolean;
  /** Configuration des bots */
  botConfig: TrainingBotConfig;
  /** Contraintes de la donne en cours */
  constraints?: TrainingDealConstraints;
}

// ============================================
// Connexion à une partie
// ============================================

/** Session différé rétro — gameplay via retro-differe:* (TrainingEngine) */
export interface RetroDiffereConnectionInfo {
  sessionId: number;
  dealIndex: number;
  seasonId: number;
  /** Reprendre une donne déjà commencée (sans refaire le fingerprint) */
  rejoin: boolean;
  totalDeals?: number;
}

/** Informations nécessaires pour se connecter à une table de jeu */
export interface GameConnectionInfo {
  /** Adresse IP du serveur de jeu (mode normal) */
  ip?: string;
  /** Port du serveur de jeu (mode normal) */
  port?: number;
  /** Type de serveur (live, libre, diff_indiv, etc.) */
  idserv?: string;
  /** ID de connexion serveur (mode "table créée") */
  serverConnectionId?: string;
  /** ID de connexion client (mode "table créée") */
  connectionId?: string;
  /** Données de la table (mode "table créée") */
  table?: GameTable;
  /** Neo table mode — if true, use Neo game engine instead of legacy Java */
  neo?: boolean;
  /** Neo table ID */
  neoTableId?: string;
  /** Spectator mode (read-only delayed replay) */
  spectatorMode?: boolean;
  /** Spectator source ID (neo:{tableId} or java:...) */
  spectatorSourceId?: string;
  /** Différé rétro classique — utilise Game.tsx avec le protocole retro-differe:* */
  retroDiffere?: RetroDiffereConnectionInfo;
  /** True si d'autres humains sont à la table (false = partie bots uniquement) */
  hasOtherHumans?: boolean;
}

// ============================================
// Notifications
// ============================================

/** Types de notifications liées au jeu */
export type GameNotificationType = 'your_turn' | 'new_game' | 'game_ended';

/** Notification liée au jeu (badge sur l'onglet "Partie") */
export interface GameNotification {
  type: GameNotificationType;
  message: string;
}

/** Types de toasts applicatifs */
export type AppToastType = 'info' | 'game' | 'chat' | 'invitation' | 'table' | 'system';

/** Toast global affiché en superposition */
export interface AppToast {
  id: string;
  message: string;
  type: AppToastType;
  /** Durée d'affichage en ms (défaut : 5000) */
  duration?: number;
  /** Onglet vers lequel naviguer au clic */
  targetTab?: TabId;
  /** Navigation bibliothèque au clic (prioritaire sur targetTab) */
  libraryNav?: LibraryNavigationRequest;
  /** Libellé du bouton d'action (ex. « Voir ») */
  actionLabel?: string;
  /** Icône ou emoji personnalisé (sinon dérivé du type) */
  icon?: string;
  /** Sous-texte descriptif */
  subtitle?: string;
}

/** Options passées à showToast pour le filtrage contextuel */
export interface ShowToastOptions {
  /** Si l'utilisateur est déjà sur cet onglet, le toast n'est pas affiché */
  sourceTab?: TabId;
}

// ============================================
// Préférences utilisateur
// ============================================

/** Choix du thème : clair, sombre, ou suivre le navigateur */
export type ThemePreference = 'light' | 'dark' | 'system';

/** Disposition des cartes en main : alignées ou en arc elliptique */
export type HandLayout = 'flat' | 'arc';

/** Texture de fond du tapis de jeu (base + textures pré-générées) */
export type TableBase =
  | 'neo'              // Gradient vert actuel
  | 'felt'             // Feutre vert (style billard / tapis de cartes)
  | 'cards-backs'      // Dos de cartes dispersés (dark/light auto)
  | 'cards-all'        // Les 78 cartes face visible (dark/light auto)
  | 'cards-trumps'     // Atouts face visible (dark/light auto)
  | 'kaleidoscope'     // Kaléidoscope 12 plis (centré)
  | 'kaleidoscope-v2'  // Kaléidoscope 8 plis (centré)
  | 'legacy-v2'        // Texture verte WebTarot 2
  | 'legacy-v1';       // Texture WebTarot 1 (à venir)

/** Surcouche animée sur le tapis */
export type TableOverlay =
  | 'none'         // Aucune surcouche
  | 'cards-plis';  // Cartes ajoutées pli par pli (animation)

/** Emblème centré sur le tapis (calque optionnel, incompatible avec kaléidoscopes) */
export type TableEmblem =
  | 'none'
  | 'wt-v1'    // WT côte à côte, variante serrée
  | 'wt-v2'    // WT côte à côte, variante large
  | 'wt-v3';   // W sur T empilé, symétrique

/** Style de tapis spécial pour les parties duplicate (compétition hors libre) */
export type DuplicateTableStyle =
  | 'dup-felt'    // Feutre + NORD/EST/SUD/OUEST aux coins + lignes
  | 'dup-neo'     // Neo + N/E/S/O doré au centre des bords + enseignes
  | 'dup-legacy'; // Legacy-v2 + N/E/S/O blanc + liseret demi-cercles

/** Préférences utilisateur persistées en localStorage */
export interface UserPreferences {
  /** Thème de l'interface */
  theme: ThemePreference;

  /** Disposition des cartes en main */
  handLayout: HandLayout;

  /** Texture de fond du tapis */
  tableBase: TableBase;

  /** Effet de cartes sur le tapis */
  tableOverlay: TableOverlay;

  /** Emblème centré sur le tapis */
  tableEmblem: TableEmblem;

  /** Basculer automatiquement vers l'onglet "Partie" quand c'est à nous de jouer */
  autoSwitchOnTurn: boolean;

  // --- Notifications cross-onglet ---

  /** Notifier des nouveaux messages chat privés quand pas sur l'onglet chat */
  notifyPrivateMessages: boolean;
  /** Notifier des nouveaux messages chat global quand pas sur l'onglet chat */
  notifyGlobalChat: boolean;
  /** Notifier des nouvelles tables créées */
  notifyNewTables: boolean;
  /** Ne pas déranger en partie contre des humains (invitations auto-déclinées) */
  doNotDisturbInGame: boolean;
  /** Même en partie, accepter les invitations si on ne joue que contre des robots */
  allowInviteIfBotsOnly: boolean;

  // --- Affichage des tables ---

  /** Icônes vintage (style WebTarot 1) dans la liste "Toutes les tables" */
  vintageTableIcons: boolean;

  /** Animer le trajet des cartes posées sur le tapis (vol depuis la position du joueur) */
  cardPlayAnimation: boolean;

  /** Orienter les cartes posées selon le siège (90° Est/Ouest, 180° Nord, 45° à 5j). */
  trickCardRotation: boolean;

  // --- Tapis duplicate ---

  /** Forcer un tapis spécial en partie duplicate */
  duplicateTableEnabled: boolean;
  /** Style du tapis duplicate */
  duplicateTableStyle: DuplicateTableStyle;

  // --- Feedback expert (aide au professeur) ---

  /** Degré de feedback actif en mode entraînement
   *  - 'none' : toggle "Aider le professeur" masqué
   *  - 'my_turn' : feedback quand c'est au tour de l'humain uniquement
   *  - 'all_turns' : feedback à chaque carte à jouer (humain ou bot)
   */
  feedbackLevel: 'none' | 'my_turn' | 'all_turns';

  /** Utiliser le modèle beta (expérimental) dans le module entraînement */
  useBetaModel: boolean;

  /** Page Résultats différé : filtrer par défaut sur « Mes tournois uniquement » */
  differeResultsOnlyMine: boolean;

  /** Page Maison : afficher la colonne « En ligne » (écrans ≥ 1500px) */
  homeOnlineListVisible: boolean;
}

/** Valeurs par défaut des préférences */
export const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'system',
  handLayout: 'flat',
  tableBase: 'neo',
  tableOverlay: 'none',
  tableEmblem: 'none',
  autoSwitchOnTurn: true,
  notifyPrivateMessages: true,
  notifyGlobalChat: false,
  notifyNewTables: false,
  doNotDisturbInGame: true,
  allowInviteIfBotsOnly: true,
  vintageTableIcons: true,
  cardPlayAnimation: true,
  trickCardRotation: false,
  duplicateTableEnabled: false,
  duplicateTableStyle: 'dup-felt',
  feedbackLevel: 'none',
  useBetaModel: false,
  differeResultsOnlyMine: true,
  homeOnlineListVisible: true,
};

// ============================================
// Session (type partagé)
// ============================================

/** Session utilisateur */
export interface Session {
  pseudo: string;
  pseudoDisplay: string;
  compet: boolean;
  hasCompetitionAccess?: boolean;
  token?: string;
  hashPass?: string;
  /**
   * True à la toute première connexion d'un nouveau compte (juste après
   * /auth/register ou /auth/oauth/complete-registration). Permet de
   * déclencher l'onboarding (choix d'avatar) avant l'accès au reste de l'app.
   * Le flag est effacé dès que l'onboarding est terminé.
   */
  isNewAccount?: boolean;
}
